// src/components/Leadership/BoardroomConfidence.js
import React from 'react';
import { Grid3x3, FileText, Shield, TrendingUp } from 'lucide-react';
import '../../styles/Leadership/BoardroomConfidence.css';

const BoardroomConfidence = () => {
  const boardroomTools = [
    {
      icon: <Grid3x3 className="tool-icon" />,
      title: "Priority Matrices",
      description: "Clear alignment of effort vs. impact",
      details: [
        "Visual impact/effort quadrants",
        "Strategic initiative ranking",
        "Resource allocation clarity",
        "Quarterly planning support"
      ],
      benefit: "Make data-driven decisions on where to invest"
    },
    {
      icon: <FileText className="tool-icon" />,
      title: "WSR/MSR Reports",
      description: "Visibility into progress and risks",
      details: [
        "Weekly Status Reports (WSR)",
        "Monthly Summary Reports (MSR)",
        "Executive dashboard views",
        "Trend analysis & forecasting"
      ],
      benefit: "Never be surprised by project status"
    },
    {
      icon: <Shield className="tool-icon" />,
      title: "Risk Management",
      description: "Frameworks that reassure investors",
      details: [
        "Risk register maintenance",
        "Mitigation strategies",
        "Compliance tracking",
        "Audit readiness"
      ],
      benefit: "Demonstrate governance to stakeholders"
    }
  ];

  const executiveMetrics = [
    { label: "Strategic Alignment", value: "95%", trend: "up" },
    { label: "Budget Adherence", value: "98%", trend: "up" },
    { label: "On-Time Delivery", value: "92%", trend: "up" },
    { label: "Stakeholder Satisfaction", value: "4.8/5", trend: "up" }
  ];

  return (
    <section className="boardroom-confidence">
      <div className="boardroom-container">
        <div className="section-header-center">
          <span className="section-badge">Boardroom Confidence</span>
          <h2 className="section-title">
            Designed for Decision Makers
          </h2>
          <p className="section-description">
            The tools, reports, and frameworks that give executives and boards 
            the confidence to invest in technology transformation.
          </p>
        </div>

        {/* Executive Metrics Dashboard */}
        <div className="executive-metrics-panel">
          <h3 className="metrics-panel-title">Live Executive Metrics</h3>
          <div className="executive-metrics-grid">
            {executiveMetrics.map((metric, index) => (
              <div key={index} className="executive-metric-card">
                <div className="metric-label-exec">{metric.label}</div>
                <div className="metric-value-exec">
                  {metric.value}
                  {metric.trend === 'up' && (
                    <TrendingUp className="metric-trend-icon" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Boardroom Tools Grid */}
        <div className="boardroom-tools-grid">
          {boardroomTools.map((tool, index) => (
            <div key={index} className="boardroom-tool-card">
              <div className="tool-header">
                <div className="tool-icon-wrapper">
                  {tool.icon}
                </div>
                <div className="tool-header-text">
                  <h3 className="tool-title">{tool.title}</h3>
                  <p className="tool-description">{tool.description}</p>
                </div>
              </div>

              <div className="tool-details-list">
                {tool.details.map((detail, idx) => (
                  <div key={idx} className="tool-detail-item">
                    <div className="tool-detail-check">✓</div>
                    <span>{detail}</span>
                  </div>
                ))}
              </div>

              <div className="tool-benefit-box">
                <div className="benefit-label">Key Benefit:</div>
                <div className="benefit-text">{tool.benefit}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Sample Priority Matrix Visual */}
        <div className="priority-matrix-section">
          <h3 className="matrix-title">Sample Priority Matrix</h3>
          <p className="matrix-subtitle">How we help you prioritize initiatives</p>
          
          <div className="priority-matrix">
            <div className="matrix-quadrant matrix-quick-wins">
              <div className="quadrant-label">Quick Wins</div>
              <div className="quadrant-desc">High Impact • Low Effort</div>
              <div className="quadrant-items">
                <div className="matrix-item">Security patches</div>
                <div className="matrix-item">API optimization</div>
              </div>
            </div>

            <div className="matrix-quadrant matrix-strategic">
              <div className="quadrant-label">Strategic</div>
              <div className="quadrant-desc">High Impact • High Effort</div>
              <div className="quadrant-items">
                <div className="matrix-item">Platform migration</div>
                <div className="matrix-item">Architecture redesign</div>
              </div>
            </div>

            <div className="matrix-quadrant matrix-fill-ins">
              <div className="quadrant-label">Fill-Ins</div>
              <div className="quadrant-desc">Low Impact • Low Effort</div>
              <div className="quadrant-items">
                <div className="matrix-item">UI tweaks</div>
                <div className="matrix-item">Documentation</div>
              </div>
            </div>

            <div className="matrix-quadrant matrix-thankless">
              <div className="quadrant-label">Thankless Tasks</div>
              <div className="quadrant-desc">Low Impact • High Effort</div>
              <div className="quadrant-items">
                <div className="matrix-item">Legacy support</div>
                <div className="matrix-item">Custom reports</div>
              </div>
            </div>
          </div>

          <div className="matrix-axes">
            <div className="axis-label axis-vertical">← Lower Effort | Higher Effort →</div>
            <div className="axis-label axis-horizontal">↑ Higher Impact | Lower Impact ↓</div>
          </div>
        </div>

        {/* Confidence Statement */}
        <div className="confidence-statement">
          <h3 className="confidence-title">
            From Uncertainty to Confidence
          </h3>
          <p className="confidence-text">
            Most technology partners leave executives guessing. We provide the visibility, 
            metrics, and governance frameworks that transform technology from a black box 
            into a competitive advantage your board can understand and trust.
          </p>
        </div>
      </div>
    </section>
  );
};

export default BoardroomConfidence;